import { type FC } from "react";
import { Circle, CircleDollarSign, Tag } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/utils/core";
import { LAWYER_PLANS } from "@/shared/constants/lawyerPlans";

import type { LawyerPlanKey } from "@/shared/constants/lawyerPlans";

interface ChoosePlanStepProps {
  selectedPlan: LawyerPlanKey | null;
  onSelect: (plan: LawyerPlanKey | null) => void;
  onNext: () => void;
  onBack: () => void;
  firstClientOfferAvailable: boolean;
}

const PLAN_KEYS: LawyerPlanKey[] = ["estate_setup", "asset_discovery", "estate_administration"];

const RadioIndicator: FC<{ selected: boolean }> = ({ selected }) => (
  <div className="hidden shrink-0 items-center pt-0.5 sm:flex">
    {selected ? (
      <div className="flex h-[18px] w-[18px] items-center justify-center rounded-full border-2 border-brand">
        <div className="h-2.5 w-2.5 rounded-full bg-brand" />
      </div>
    ) : (
      <Circle className="h-[18px] w-[18px] text-stone-300" />
    )}
  </div>
);

export const ChoosePlanStep: FC<ChoosePlanStepProps> = ({
  selectedPlan,
  onSelect,
  onNext,
  onBack,
  firstClientOfferAvailable,
}) => {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-col gap-6 overflow-y-auto bg-subtle p-6">
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-1">
            <h4 className="text-xl font-semibold">Recommend a plan</h4>
            <p className="text-sm text-secondary">
              Choose the plan that fits this estate. Your client sees it as your recommendation and
              a clear path forward.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <CircleDollarSign className="h-4 w-4 text-brand" />
            <p className="text-2xs text-brand">
              Paid plans are billed as an estate expense. No charge to your firm.
            </p>
          </div>
        </div>

        {firstClientOfferAvailable && (
          <div className="flex items-start gap-3 rounded-lg border border-brand bg-brand-subtle p-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border border-primary bg-white">
              <Tag className="h-4 w-4 text-brand" />
            </div>
            <p className="text-sm">
              <span className="font-semibold">First-client offer:</span> your first client&apos;s
              Estate Setup and Asset Search are on us, a $3,000 value. IRS transcript access
              included.
            </p>
          </div>
        )}

        <div className="flex flex-col gap-3">
          {PLAN_KEYS.map((key) => {
            const plan = LAWYER_PLANS[key];
            const isSelected = selectedPlan === key;
            const price = firstClientOfferAvailable ? plan.firstClientPrice : plan.price;
            const isIncluded = firstClientOfferAvailable && plan.firstClientPrice === 0;
            const isDiscounted =
              firstClientOfferAvailable &&
              plan.firstClientPrice > 0 &&
              plan.firstClientPrice < plan.price;

            return (
              <button
                key={key}
                type="button"
                onClick={() => onSelect(key)}
                className={cn(
                  "flex gap-2 rounded-lg bg-white p-4 text-left transition-colors",
                  isSelected
                    ? "border-2 border-brand"
                    : "border border-primary hover:border-stone-300",
                )}
              >
                <RadioIndicator selected={isSelected} />
                <div className="flex flex-1 flex-col gap-2 min-w-0">
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold">{plan.name}</span>
                    {plan.sources && <span className="text-2xs text-tertiary">{plan.sources}</span>}
                  </div>
                  <span className="text-sm text-secondary">{plan.description}</span>
                  <span className="text-2xs text-tertiary">{plan.savingsNote}</span>
                </div>
                <div className="flex shrink-0 flex-col items-end">
                  {isIncluded ? (
                    <>
                      <span className="text-sm font-medium text-brand">Included</span>
                      <span className="text-2xs text-tertiary line-through">
                        Regular ${plan.price.toLocaleString()}
                      </span>
                    </>
                  ) : isDiscounted ? (
                    <>
                      <span className="text-sm font-medium text-brand">
                        ${price.toLocaleString()}
                      </span>
                      <span className="text-2xs text-tertiary line-through">
                        Regular ${plan.price.toLocaleString()}
                      </span>
                      <span className="text-2xs text-tertiary">Estate pays the upgrade only</span>
                    </>
                  ) : (
                    <span className="text-sm font-medium">${price.toLocaleString()}</span>
                  )}
                </div>
              </button>
            );
          })}

          <button
            type="button"
            onClick={() => onSelect(null)}
            className={cn(
              "flex gap-2 rounded-lg bg-white p-4 text-left transition-colors",
              selectedPlan === null
                ? "border-2 border-brand"
                : "border border-primary hover:border-stone-300",
            )}
          >
            <RadioIndicator selected={selectedPlan === null} />
            <div className="flex flex-1 flex-col gap-2">
              <span className="text-sm font-semibold">Invite without a recommendation</span>
              <span className="text-sm text-secondary">
                The client reviews the plans and selects one at activation
              </span>
              <span className="text-2xs text-tertiary">
                Without a recommendation, your client chooses from the pricing page alone
              </span>
            </div>
          </button>
        </div>
      </div>

      <div className="flex justify-between border-t border-primary px-6 py-4">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button variant="brand" onClick={onNext}>
          Continue
        </Button>
      </div>
    </div>
  );
};
